import eventService from './service'
import { fieldState } from '@shared/stores/fieldScope'
import { eventBus } from '@shared/stores/globalState'
import { authService } from '@shared/services/auth/authService'

import type { User } from '@shared/types/models/user'

type EventServiceStatus = 'connected' | 'disconnected' | 'error'

/**
 * EventService 初始化管理器
 * 負責管理事件服務的生命週期，包括啟動、停止和重新連接
 */
class EventServiceInitializer {
  private isInitialized: boolean = false
  private fieldStore: ReturnType<typeof fieldState> | null = null
  private userLoginHandler: ((user: User) => Promise<void>) | null = null
  private userLogoutHandler: (() => void) | null = null
  private retryCount: number = 0
  private maxRetries: number = 3

  /**
   * 初始化監聽器（只設置監聽器，不啟動服務）
   */
  async init(): Promise<void> {
    if (this.isInitialized) return

    this.fieldStore = fieldState()
    this.setupEventBusListeners()
    this.isInitialized = true
  }

  /**
   * 啟動事件服務（供 App.vue afterLogin() 呼叫）
   * 統一入口，整合緩存載入 + Realtime 訂閱
   */
  async start(): Promise<void> {
    const user = authService.currentUser
    if (!user) return

    const userId = user.uid || user.id
    if (!userId) return

    try {
      await this.waitForFieldStoreReady()
      await eventService.start(userId)
      this.retryCount = 0
      eventBus.emit('eventService:statusChanged', 'connected' as EventServiceStatus)
    } catch (error) {
      console.error('[EventServiceInit] 啟動失敗:', error)
      eventBus.emit('eventService:statusChanged', 'error' as EventServiceStatus)
    }
  }

  /**
   * 設置 eventBus 監聽器
   */
  private setupEventBusListeners(): void {
    this.userLoginHandler = async (user: User) => {
      if (user) await this.startEventService(user)
    }

    this.userLogoutHandler = () => this.stopEventService()

    eventBus.on('user:login', this.userLoginHandler)
    eventBus.on('user:logout', this.userLogoutHandler)

    // 監聽組織變更事件，更新未讀計數
    eventBus.on('org:changed', async () => {
      if (eventService.isInitialized) {
        await eventService.updateUnreadCounts()
      }
    })

    // 監聽 Realtime 徹底失敗，嘗試完整重啟（防止重複觸發）
    let isRestarting = false
    eventBus.on('event:health:failed', async () => {
      if (isRestarting) return
      isRestarting = true
      console.warn('[EventServiceInit] Realtime 完全斷線，嘗試完整重啟')
      try {
        await this.restart()
      } finally {
        isRestarting = false
      }
    })
  }

  /**
   * @deprecated 已由 afterLogin() 呼叫 start() 取代
   */
  async tryInitialize(): Promise<void> {
    // 保留空方法以維持向後兼容
  }

  /**
   * 啟動事件服務（供 auth listener 使用）
   */
  private async startEventService(user: User, retryCount: number = 0): Promise<void> {
    try {
      const userId = user.uid || user.id
      if (!userId) return

      await this.waitForFieldStoreReady()
      await eventService.start(userId)
      this.retryCount = 0
      eventBus.emit('eventService:statusChanged', 'connected' as EventServiceStatus)
    } catch (error) {
      if (retryCount < this.maxRetries - 1) {
        const delay = 2000 * (retryCount + 1)
        setTimeout(() => this.startEventService(user, retryCount + 1), delay)
      } else {
        console.error('[EventServiceInit] 啟動失敗:', error)
        eventBus.emit('eventService:statusChanged', 'error' as EventServiceStatus)
      }
    }
  }

  /**
   * 停止事件服務
   */
  private stopEventService(): void {
    try {
      eventService.destroy()
      this.retryCount = 0
      eventBus.emit('eventService:statusChanged', 'disconnected' as EventServiceStatus)
    } catch (error) {
      eventBus.emit('eventService:statusChanged', 'error' as EventServiceStatus)
    }
  }

  /**
   * 等待 field store 準備就緒
   */
  private async waitForFieldStoreReady(): Promise<void> {
    // EventService 不需要等待 fieldStore，可以獨立啟動
    return Promise.resolve()
  }

  /**
   * 手動重新啟動事件服務（用於錯誤恢復）
   */
  async restart(): Promise<void> {
    this.stopEventService()
    if (authService.currentUser) {
      await this.startEventService(authService.currentUser)
    }
  }

  /**
   * 停止監聽器
   */
  stop(): void {
    this.stopEventService()

    if (this.userLoginHandler) {
      eventBus.off('user:login', this.userLoginHandler)
      this.userLoginHandler = null
    }
    if (this.userLogoutHandler) {
      eventBus.off('user:logout', this.userLogoutHandler)
      this.userLogoutHandler = null
    }

    this.isInitialized = false
    this.fieldStore = null
    this.retryCount = 0
  }
}

// 建立單例
const eventServiceInitializer = new EventServiceInitializer()

export default eventServiceInitializer
